import { colors, ColorType, getColorStylesByType } from '@mm/utils';
import classnames from 'classnames';
import { isBoolean, isFunction, isNil } from 'lodash';
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Popover from 'react-tiny-popover';
import styles from './Tooltip.module.css';

type ChildrenRenderProp = (closeToolTip?: () => void) => ReactNode;

type Props = {
  title?: string;
  children?: ChildrenRenderProp | ReactNode | Element | string;
  onClickOutside?: (e: MouseEvent, closePopup: Function) => void;
  anchorElement: ReactNode;
  position?: 'top' | 'bottom' | 'right' | 'left';
  align?: 'start' | 'center' | 'end';
  color?: 'light' | 'dark';
  css?: {
    container?: string;
    content?: string;
    popoverContainer?: string;
    arrow?: string;
    title?: string;
  };
  useHover?: boolean;
  toolTipDisabled?: boolean;
  isVisible?: boolean;
  enableReposition?: boolean;
  withoutLine?: boolean;
  tipLineColor?: string;
  setIsVisible?: Dispatch<SetStateAction<boolean>>;
  type?: ColorType;
  noDelayClose?: boolean;
};

const HOVER_CLOSE_DELAY_MS = 300;

export const Tooltip = (props: Props) => {
  const {
    toolTipDisabled,
    anchorElement,
    title,
    children,
    css,
    useHover = false,
    position = 'bottom',
    align = 'center',
    isVisible,
    enableReposition,
    withoutLine = false,
    tipLineColor,
    setIsVisible,
    onClickOutside,
    type = 'info',
    color,
    noDelayClose,
  } = props;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const localTooltipVisible = isNil(isVisible) ? tooltipVisible : isVisible;
  const setLocalTooltipVisible = setIsVisible
    ? setIsVisible
    : setTooltipVisible;

  useEffect(() => {
    if (isBoolean(isVisible)) {
      setLocalTooltipVisible(isVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const { color: colorFromType } = getColorStylesByType(type);
  const style = {
    backgroundColor:
      tipLineColor || (type === 'info' ? colors.accentBlue : colorFromType),
  };

  const containerStyles = classnames(
    styles.tipArea,
    css?.container,
    styles[`tipArea--${color}`],
  );
  const lineStyles = classnames(
    styles.tipAreaBottomLine,
    styles[`tipAreaBottomLine--${position}`],
  );
  const arrowStyles = classnames(
    styles.tipAreaArrowIcon,
    styles[`tipAreaArrow--${position}`],
    styles[`tipAreaArrow--align--${align}`],
    css?.arrow,
  );
  const contentStyles = classnames(styles.main, css?.content);

  const popoverClassName = classnames(
    'react-tiny-popover-container',
    css?.popoverContainer,
  );

  const memoizedCloseToolTip = useCallback(() => {
    setLocalTooltipVisible(false);
  }, [setLocalTooltipVisible]);

  const memoizedOnClickOutside = useCallback(
    (e: MouseEvent) => {
      if (onClickOutside) {
        onClickOutside(e, () => setLocalTooltipVisible(false));
        return;
      }
      const isInsideClick = e.composedPath().some((target: Element) => {
        return target.className === 'react-tiny-popover-container';
      });
      if (!isInsideClick) {
        setLocalTooltipVisible(false);
      }
    },
    [setLocalTooltipVisible, onClickOutside],
  );

  const memoizedOpenToolTip = useCallback(() => {
    setLocalTooltipVisible(true);
  }, [setLocalTooltipVisible]);

  const memoizedOnClickHandler = useCallback(() => {
    setLocalTooltipVisible(prevTooltipVisible => !prevTooltipVisible);
  }, [setLocalTooltipVisible]);

  const closeTimeoutRef = useRef<number | null>(null);

  const onPointerLeave = () => {
    if (noDelayClose) {
      memoizedCloseToolTip();
      return;
    }
    closeTimeoutRef.current = window?.setTimeout(
      memoizedCloseToolTip,
      HOVER_CLOSE_DELAY_MS,
    );
  };

  const onPointerEnter = () => {
    if (closeTimeoutRef.current) {
      window?.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (!isVisible) {
      memoizedOpenToolTip();
    }
  };

  const propsForHoverHandling = { onPointerEnter, onPointerLeave };

  const propsForClickHandling = {
    onClick: memoizedOnClickHandler,
  };

  const handlingPropsAnchor = toolTipDisabled
    ? {}
    : useHover
    ? propsForHoverHandling
    : propsForClickHandling;

  const handlingPropsContent = useHover ? propsForHoverHandling : {};

  return (
    <Popover
      disableReposition={!enableReposition}
      align={align}
      position={position}
      isOpen={localTooltipVisible && !toolTipDisabled}
      onClickOutside={memoizedOnClickOutside}
      containerClassName={popoverClassName}
      containerStyle={{ zIndex: '1000002', overflow: 'visible' }}
      content={() => (
        <div className={containerStyles} {...handlingPropsContent}>
          <div className={arrowStyles} />

          {title && (
            <div className={classnames(styles.tipAreaTitle, css?.title)}>
              {title}
            </div>
          )}

          <div className={styles.tipAreaBody}>
            {isFunction(children) ? children(memoizedCloseToolTip) : children}
          </div>

          {!withoutLine && <div className={lineStyles} style={style} />}
        </div>
      )}
    >
      <div className={contentStyles} {...handlingPropsAnchor}>
        {anchorElement}
      </div>
    </Popover>
  );
};
